import React from 'react';
import { createPortal } from 'react-dom';
import { getCountryCallingCode } from 'react-phone-number-input';

import * as styled from './styles';

type Option = { value?: string; label: string; divider?: boolean };

type Props = {
  value?: string;
  onChange: (value?: string) => void;
  options: Option[];
  iconComponent?: React.ElementType;
  disabled?: boolean;
  readOnly?: boolean;
  'aria-label'?: string;
};

const DROPDOWN_WIDTH_PX = 280;
const VIEWPORT_MARGIN_PX = 8;
const DROPDOWN_GAP_PX = 6;

const callingCode = (country?: string): string => {
  if (!country) return '';
  try {
    return getCountryCallingCode(country as never);
  } catch {
    return '';
  }
};

export const CountrySelectSearch = ({
  value,
  onChange,
  options,
  iconComponent: Icon,
  disabled,
  readOnly,
  ...rest
}: Props) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [coords, setCoords] = React.useState<{
    top: number;
    left: number;
  } | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const reposition = React.useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const maxLeft = window.innerWidth - DROPDOWN_WIDTH_PX - VIEWPORT_MARGIN_PX;
    setCoords({
      top: rect.bottom + DROPDOWN_GAP_PX,
      left: Math.max(VIEWPORT_MARGIN_PX, Math.min(rect.left, maxLeft)),
    });
  }, []);

  React.useEffect(() => {
    if (!open) return undefined;
    reposition();
    const handlePointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, reposition]);

  const selectable = React.useMemo(
    () => options.filter((option) => option.value),
    [options],
  );

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return selectable;
    return selectable.filter((option) => {
      const dial = `+${callingCode(option.value)}`;
      return (
        option.label.toLowerCase().includes(q) ||
        dial.includes(q) ||
        (option.value ?? '').toLowerCase().startsWith(q)
      );
    });
  }, [selectable, query]);

  const selected = options.find((option) => option.value === value);

  const select = (next?: string) => {
    onChange(next);
    setOpen(false);
    setQuery('');
  };

  return (
    <styled.CountryRoot>
      <styled.CountryButton
        ref={buttonRef}
        type="button"
        disabled={disabled || readOnly}
        onClick={() => setOpen((prev) => !prev)}
        aria-label={rest['aria-label'] ?? 'Select country'}
        aria-expanded={open}
      >
        {Icon && value ? (
          <Icon country={value} label={selected?.label} />
        ) : (
          <styled.CountryGlobe aria-hidden>🌐</styled.CountryGlobe>
        )}
        <styled.CountryChevron aria-hidden>▾</styled.CountryChevron>
      </styled.CountryButton>

      {open &&
        coords &&
        createPortal(
          <styled.CountryDropdown
            ref={dropdownRef}
            style={{ top: coords.top, left: coords.left }}
          >
            <styled.CountrySearch
              autoFocus
              type="text"
              placeholder="Search country"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <styled.CountryList>
              {filtered.length === 0 ? (
                <styled.CountryEmpty>No matches</styled.CountryEmpty>
              ) : (
                filtered.map((option) => (
                  <styled.CountryOption
                    key={option.value}
                    type="button"
                    $active={option.value === value}
                    onClick={() => select(option.value)}
                  >
                    {Icon && (
                      <Icon country={option.value} label={option.label} />
                    )}
                    <styled.CountryOptionName>
                      {option.label}
                    </styled.CountryOptionName>
                    <styled.CountryOptionDial>
                      +{callingCode(option.value)}
                    </styled.CountryOptionDial>
                  </styled.CountryOption>
                ))
              )}
            </styled.CountryList>
          </styled.CountryDropdown>,
          document.body,
        )}
    </styled.CountryRoot>
  );
};
